import { Link } from "react-router-dom";
import SiteLayout from "@/components/SiteLayout";
import Reveal from "@/components/Reveal";
import SEO, { breadcrumbJsonLd, faqJsonLd } from "@/components/SEO";
import TrustedReferences from "@/components/TrustedReferences";

const FAQ = [
  {
    q: "What kinds of behavioral-health organizations does Catalyst serve?",
    a: "We work with substance-use programs (residential, PHP, IOP, outpatient), mental-health providers, autism/ABA agencies, and specialty programs from single-site startups through multi-site operators nationwide.",
  },
  {
    q: "How does Catalyst help with accreditation — CARF, Joint Commission, COA?",
    a: "We run readiness assessments, close standards gaps, build the required policies and quality-improvement infrastructure, and coach leadership through mock surveys so the on-site survey feels routine, not stressful.",
  },
  {
    q: "Do you offer fractional HR leadership?",
    a: "Yes. Through Catalyst HR Consulting we provide fractional HR directors, compliance audits, investigations, handbook builds, and full-cycle talent strategy — designed specifically for the pace and regulatory context of behavioral healthcare.",
  },
  {
    q: "Can you help us open a new behavioral-health program?",
    a: "Absolutely. Catalyst Continuum handles end-to-end program development — market analysis, licensure, staffing plans, clinical curriculum, payer contracts, and go-live operations across the continuum of care.",
  },
  {
    q: "How quickly can we get started after our first conversation?",
    a: "Most engagements begin within 2–3 weeks of a signed agreement. Discovery calls are free and we can typically schedule one within a few business days of the request.",
  },
  {
    q: "Do you serve organizations outside Arizona?",
    a: "Yes. We serve behavioral-health organizations across the United States. Most engagements combine virtual sessions with periodic on-site visits when the scope requires it.",
  },
  {
    q: "What does an engagement typically cost?",
    a: "Every organization is different — we structure engagements around defined scope and outcomes rather than hourly billing. We share investment ranges on the discovery call once we understand the scope you need.",
  },
];

const SOLUTIONS_JSONLD = {
  "@context": "https://schema.org",
  "@graph": [
    breadcrumbJsonLd([["Home", "/"], ["Solutions"]]),
    faqJsonLd(FAQ),
  ],
};

const SOLUTIONS = [
  { n: "01", title: "Accreditation", body: "Joint Commission, CARF, and COA survey readiness, mock surveys, and corrective action systems." },
  { n: "02", title: "HR", body: "Fractional HR leadership, compliance audits, investigations, and handbooks tailored to healthcare." },
  { n: "03", title: "Operations", body: "Staffing models, workflow design, utilization review, productivity dashboards, and capacity planning." },
  { n: "04", title: "Clinical Excellence", body: "Documentation systems, evidence-based curricula, outcome tracking, and quality improvement." },
  { n: "05", title: "Leadership", body: "Executive coaching, fractional leadership, and cohort-based leadership academies." },
  { n: "06", title: "Compliance", body: "State licensing, corporate compliance programs, audit response, and risk mitigation." },
  { n: "07", title: "Technology", body: "EHR selection, dashboard implementation, and workflow automation for lean teams." },
];

export default function Solutions() {
  return (
    <SiteLayout branch="main">
      <SEO
        title="Solutions"
        path="/solutions"
        description="Seven capabilities Catalyst delivers to behavioral-health organizations: accreditation, HR, operations, clinical excellence, leadership, compliance, and technology."
        jsonLd={SOLUTIONS_JSONLD}
      />
      <section className="section bg-[var(--navy)] text-white">
        <Reveal className="max-w-4xl">
          <span className="eyebrow">SOLUTIONS</span>
          <h1 className="font-display mt-5 text-[clamp(40px,5vw,68px)] leading-[1.05]">
            Seven capabilities. <em className="italic text-[var(--gold)]">One connected strategy.</em>
          </h1>
          <p className="text-neutral-300 mt-6 max-w-2xl">
            Where behavioral health providers see the biggest impact when they work with Catalyst.
          </p>
        </Reveal>
      </section>

      <section className="section bg-white">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-[1300px] mx-auto">
          {SOLUTIONS.map((s, i) => (
            <Reveal key={s.n} delay={i * 60}>
              <article className="card-editorial min-h-[240px]" data-testid={`sol-${s.n}`}>
                <div className="text-[var(--gold)] text-[10px] tracking-widest font-bold mb-3">{s.n}</div>
                <h3 className="font-display text-2xl text-[var(--navy)] mb-3">{s.title}</h3>
                <p className="text-[var(--muted)] text-sm leading-relaxed">{s.body}</p>
              </article>
            </Reveal>
          ))}
        </div>
        <div className="text-center mt-14">
          <Link to="/book" className="btn btn-navy">Explore a Solution</Link>
        </div>
      </section>

      <TrustedReferences
        title="We build against the standards that survey teams use every day."
        refs={[
          { agency: "Joint Commission", label: "Behavioral Health Care & Human Services standards", href: "https://www.jointcommission.org/accreditation-and-certification/health-care-settings/behavioral-health-care/" },
          { agency: "CARF", label: "Standards Manuals for Behavioral Health", href: "https://www.carf.org/programs/behavioral-health/" },
          { agency: "SAMHSA", label: "Evidence-based practices & program directories", href: "https://www.samhsa.gov/" },
          { agency: "CMS", label: "Conditions of Participation for behavioral health providers", href: "https://www.cms.gov/" },
          { agency: "NABH", label: "National Association for Behavioral Healthcare", href: "https://www.nabh.org/" },
          { agency: "NIATx", label: "Process-improvement tools for BH programs", href: "https://niatx.wisc.edu/" },
        ]}
      />
    </SiteLayout>
  );
}
